// Top bar: the leader's state as Civ-style yields (each value sourced and dated), the date and
// turn, and the menu buttons. Under it, when scrubbed into the past, a banner says so.
//
//   (T) │ ◉ GDP•  ◉ Hard power•  ◉ Tech•  ◉ Space•  … │ 28 SEP 2026 · TURN 1,396 │ ⚖ 🏆 📖 ≡
//        └ gold dot = sourced: hover lists publishers, click opens the Sources drawer

import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';
import { daysBetween } from '../core/dates.ts';
import type { SourceId } from '../core/schema.ts';
import type { PanelId, Services } from '../services/api.ts';
import type { VictoryTrack } from '../world/api.ts';
import { useServices } from './context.ts';
import { fmtDate, fmtInt, fmtStat, isStale, turnOf } from './format.ts';
import { Icon, type IconName } from './icons.tsx';
import { emblemOf, inOffice, leaderEmblem, lookOf, perspectiveOf, reportOf, selfIso, topLeader } from './nation.ts';
import { ClaimLabel, Medallion, SourceChip, openSources, useTip } from './parts.tsx';
import { registerSources, switcherOpen } from './store.ts';
import { TRACK_RULE, fmtTrack, rankAt } from './tracks.ts';
import { whatIfHref } from './whatif.ts';

const TIP_SOURCES = 3;
const WHAT_IF_NOTE = 'Fork the real world on this date in the what-if sandbox. Everything after it is simulated.';
const DAYS_PER_YEAR = 365.25;

const ROLE_TEXT: Record<string, string> = {
  head_of_state: 'head of state',
  head_of_government: 'head of government',
  paramount_leader: 'paramount leader',
};

/** One yield value as shown in the bar. */
interface YieldValue {
  text: string;
  year: number;
  sources: SourceId[];
  /** Tooltip lines, e.g. the victory breakdown or the indicator unit. */
  detail: string;
  rank?: { pos: number; of: number; tied: boolean };
}

interface YieldDef {
  id: string;
  name: string;
  icon: IconName;
  color: string;
  read(services: Services, iso3: string): YieldValue | null;
}

// Indicator ids differ by bake version; the first id present wins.
const STAT_IDS = {
  gdp: ['gdp', 'gdp_nominal', 'gdp_usd', 'NY.GDP.MKTP.CD', 'NGDPD'],
  power: ['hard_power', 'milex_share', 'military_share', 'sipri_share'],
  population: ['population', 'pop', 'SP.POP.TOTL', 'LP'],
};

function statYield(ids: readonly string[]) {
  return (services: Services, iso3: string): YieldValue | null => {
    const stats = reportOf(services.world, iso3)?.stats;
    if (!stats) {
      return null;
    }
    const id = ids.find((k) => stats[k] !== undefined);
    if (!id) {
      return null;
    }
    const s = stats[id];
    return { text: fmtStat(s.value, s.unit), year: s.year, sources: s.sources, detail: '' };
  };
}

function trackYield(track: VictoryTrack) {
  return (services: Services, iso3: string): YieldValue | null => {
    const entries = services.world.victory(track);
    const pos = entries.findIndex((e) => e.iso3 === iso3);
    if (pos < 0) {
      return null;
    }
    const e = entries[pos];
    return {
      text: fmtTrack(track, e.progress),
      year: e.dataYear,
      sources: e.sources,
      detail: e.detail,
      rank: { ...rankAt(entries, pos), of: entries.length },
    };
  };
}

const YIELDS: YieldDef[] = [
  { id: 'gdp', name: 'GDP', icon: 'economy', color: 'var(--y-economy)', read: statYield(STAT_IDS.gdp) },
  { id: 'power', name: 'Hard power', icon: 'power', color: 'var(--y-power)', read: statYield(STAT_IDS.power) },
  { id: 'tech', name: 'Tech', icon: 'science', color: 'var(--y-science)', read: trackYield('tech') },
  { id: 'space', name: 'Space', icon: 'launch', color: 'var(--y-space)', read: trackYield('space') },
  { id: 'culture', name: 'Culture', icon: 'culture', color: 'var(--y-culture)', read: trackYield('culture') },
  { id: 'diplomacy', name: 'Diplomacy', icon: 'diplomacy', color: 'var(--y-diplomacy)', read: trackYield('diplomatic') },
  { id: 'population', name: 'Population', icon: 'population', color: 'var(--y-population)', read: statYield(STAT_IDS.population) },
];

// What each yield means, for the tooltip (no numbers here).
const YIELD_NOTE: Record<string, string> = {
  gdp: 'Gross domestic product, current US dollars.',
  power: 'Military spending (constant 2024 US$) as a share of the SIPRI world total of the same year where SIPRI gives one, otherwise of the sum of each state\'s latest value.',
  tech: 'Tech-frontier track: 0.4 AI models + 0.3 TOP500 + 0.3 papers, each a share of the world total.',
  space: 'Space-ladder rungs achieved, out of 9. Not a share.',
  culture: 'Culture track: 0.5 tourist arrivals + 0.3 World Heritage + 0.2 Olympic medals, each a share of the world total.',
  diplomacy: `Diplomatic track. ${TRACK_RULE.diplomatic.formula}.`,
  population: 'Residents.',
};

/** `sound`: the music control the composition root slots in beside the menu. */
export function TopBar({ sound }: { sound?: ComponentChildren }) {
  const services = useServices();
  const { state, world } = services;
  const iso3 = selfIso(services);
  if (!iso3) {
    return null;
  }

  const date = state.date.value;
  const present = date >= state.asOf.value;

  return (
    <>
      <div class="wc-topbar wc-live">
        <button
          type="button"
          class="wc-topbar-medal"
          aria-label="Switch perspective"
          aria-expanded={switcherOpen.value}
          onClick={() => (switcherOpen.value = !switcherOpen.value)}
        >
          <Medallion {...emblemOf(world, iso3, 'person')} size={58} />
        </button>
        {switcherOpen.value ? <PerspectiveSwitcher /> : null}

        <div class="wc-yields">
          {YIELDS.map((y) => (
            <YieldItem key={y.id} def={y} iso3={iso3} />
          ))}
        </div>

        <div class="wc-topbar-spacer" />

        <div class="wc-topbar-date">
          <span class="wc-topbar-day">{fmtDate(date)}</span>
          <span class="wc-topbar-sep">◆</span>
          <span class="wc-topbar-turn">
            Turn <b class="wc-num">{fmtInt(turnOf(date))}</b>
          </span>
          {present ? <span class="wc-present" title="Present day: the latest sourced date">Present day</span> : null}
        </div>

        {sound ? <div class="wc-topbar-sound">{sound}</div> : null}

        <nav class="wc-menu">
          <MenuButton panel="diplomacy" icon="scales" label="Diplomacy" />
          <MenuButton panel="victory" icon="victory" label="Victory" />
          <MenuButton panel="civilopedia" icon="source" label="Sources" />
          <MenuButton panel="log" icon="log" label="Event log" />
        </nav>
      </div>
      <div class="wc-banners">
        {present ? null : <HistoryBanner />}
        <PerspectiveCaption />
      </div>
    </>
  );
}

// Under the medallion when the perspective's leader is not in office at the date shown:
// who the perspective is, and who actually held office then (CountryReport.leaders).
function PerspectiveCaption() {
  const { state, world } = useServices();
  const id = state.leader.value;
  const p = id ? perspectiveOf(world, id) : null;
  if (!p || inOffice(world, p)) {
    return null;
  }

  const holder = topLeader(world, p.iso3);
  return (
    <div class="wc-persp-caption wc-live">
      <span>
        Perspective: <b>{p.name}</b> <span class="wc-year">in office from {fmtDate(p.term.from)}</span>
      </span>
      <span class="wc-persp-sep">◆</span>
      {holder ? (
        <span>
          At this date: <b>{holder.name}</b> <span class="wc-year">{ROLE_TEXT[holder.role] ?? holder.role}</span>{' '}
          <SourceChip ids={holder.sources} />
        </span>
      ) : (
        <span class="wc-year">No sourced officeholder at this date</span>
      )}
    </div>
  );
}

// Historical mode: say how far back we are, offer the way home, and the door into the sandbox
// forked on this date (leading the selected state, else the perspective's own).
function HistoryBanner() {
  const services = useServices();
  const { state, actions } = services;
  const date = state.date.value;
  const lead = state.selection.value?.iso3 ?? selfIso(services);
  const asOf = state.asOf.value;
  const years = Math.floor(daysBetween(date, asOf) / DAYS_PER_YEAR);
  const ago = years >= 1 ? `${years} year${years > 1 ? 's' : ''} before present` : 'before present';

  return (
    <div class="wc-history wc-live" role="status">
      <Icon name="calendar" size={14} />
      <span>
        Viewing <b>{fmtDate(date)}</b> · {ago}
      </span>
      <button type="button" class="wc-history-btn" onClick={() => actions.setDate(asOf)}>
        Return to present
        <Icon name="next" size={13} />
      </button>
      <a class="wc-whatif" href={whatIfHref(date, lead)} title={WHAT_IF_NOTE}>
        What if from here?
      </a>
    </div>
  );
}

// "View as": the same world through another playable leader. Numbers never change, only
// framing (POV borders, relations, agenda) — ARCHITECTURE §5.
function PerspectiveSwitcher() {
  const { world, state, actions } = useServices();
  const current = state.leader.value;
  return (
    <div class="wc-switcher wc-panel" role="menu">
      <div class="wc-switcher-title">View the world as</div>
      {world.perspectives().map((p) => {
        const look = lookOf(world, p.iso3);
        return (
          <button
            key={p.id}
            type="button"
            role="menuitemradio"
            aria-checked={p.id === current}
            class="wc-switcher-item"
            onClick={() => {
              switcherOpen.value = false;
              actions.chooseLeader(p.id);
            }}
          >
            <Medallion {...leaderEmblem(world, p)} size={34} />
            <span>
              <b>{p.name}</b>
              <i>{look.name}</i>
            </span>
          </button>
        );
      })}
      <div class="wc-switcher-note">Switching changes framing only, never the numbers.</div>
    </div>
  );
}

function YieldItem({ def, iso3 }: { def: YieldDef; iso3: string }) {
  const services = useServices();
  const date = services.state.date.value;
  let value: YieldValue | null = null;
  try {
    value = def.read(services, iso3);
  } catch {
    value = null;
  }

  // The bar shows no chips: a gold dot marks a sourced value, the tooltip names the sources
  // and a click opens them in the drawer. Sources still count as "on screen".
  const ids = value?.sources ?? [];
  const key = ids.join('|');
  useEffect(() => registerSources(ids), [key]);

  const tipHandlers = useTip(() => <YieldTip def={def} value={value} />, 'below');
  const stale = value ? isStale(value.year, date) : false;
  const sourced = ids.length > 0;

  return (
    <button
      type="button"
      class={stale ? 'wc-yield wc-stale' : 'wc-yield'}
      style={{ '--yc': def.color }}
      aria-label={`${def.name}: ${value ? value.text : 'no sourced value'}`}
      onClick={() => sourced && openSources(ids)}
      {...tipHandlers}
    >
      <span class="wc-yield-icon">
        <Icon name={def.icon} size={17} />
      </span>
      <span class="wc-yield-text">
        <span class="wc-yield-name">{def.name}</span>
        <span class="wc-yield-val wc-num">
          {value ? value.text : '—'}
          {stale && value ? <span class="wc-yield-year"> {value.year}</span> : null}
          {sourced ? <span class="wc-src-dot" aria-hidden="true" /> : null}
        </span>
      </span>
    </button>
  );
}

function YieldTip({ def, value }: { def: YieldDef; value: YieldValue | null }) {
  const { world } = useServices();
  let body: ComponentChildren = <div class="wc-empty">No sourced value for this date.</div>;
  if (value) {
    body = (
      <>
        <div class="wc-tip-big wc-num" style={{ color: def.color }}>
          {value.text}
        </div>
        {value.rank ? (
          <div class="wc-tip-row">
            <span>World rank</span>
            <b>
              #{value.rank.pos} of {value.rank.of}
              {value.rank.tied ? ' (tied)' : ''}
            </b>
          </div>
        ) : null}
        <div class="wc-tip-row">
          <span>Data year</span>
          <b>{value.year}</b>
        </div>
        {value.detail ? <div class="wc-tip-detail">{value.detail}</div> : null}
        <ClaimLabel kind="data" />
        <div class="wc-tip-sources">
          {value.sources.slice(0, TIP_SOURCES).map((id) => {
            const src = world.source(id);
            return (
              <div class="wc-tip-src" key={id}>
                <b>{src?.publisher ?? 'Unresolved id'}</b>
                <span>{src?.title ?? id}</span>
              </div>
            );
          })}
        </div>
        <div class="wc-tip-hint">Click to open in Sources</div>
      </>
    );
  }
  return (
    <>
      <div class="wc-tip-title">{def.name}</div>
      <div class="wc-tip-note">{YIELD_NOTE[def.id]}</div>
      {body}
    </>
  );
}

function MenuButton({ panel, icon, label }: { panel: PanelId; icon: IconName; label: string }) {
  const { state, actions } = useServices();
  const open = state.panel.value === panel;
  const tipHandlers = useTip(() => <div class="wc-tip-title">{label}</div>, 'below');
  return (
    <button
      type="button"
      class="wc-ibtn wc-menu-btn"
      aria-label={label}
      aria-pressed={open}
      onClick={() => actions.openPanel(open ? 'none' : panel)}
      {...tipHandlers}
    >
      <Icon name={icon} size={18} />
    </button>
  );
}
